/**
 * Generar una semana de comidas según un tipo de dieta.
 *
 * Es una propuesta, no una prescripción: rellena los huecos vacíos con
 * recetas del hogar y lo que sale son platos normales del plan, que se
 * cambian o se quitan como cualquier otro. Lo que ya estaba puesto se
 * respeta y cuenta para el equilibrio.
 *
 * Todo se deduce de los ingredientes de cada receta (grupos y gramos), igual
 * que el plato del día: no se guarda ninguna etiqueta de "ligero" o
 * "proteico" que luego se quede vieja al editar la receta. Y no se habla de
 * calorías porque no las tenemos: inventarlas es justo lo que no se hace.
 *
 * Es puro y determinista: con la misma semilla sale la misma semana, que es
 * lo que permite probarlo.
 */
import {
  MEAL_SLOTS,
  PROTEIN_GROUPS,
  gramsOf,
  type Course,
  type HouseholdEvent,
  type MealSlot,
  type ProteinGroup,
} from './events.js'
import {
  GROUPS_BY_BUCKET,
  planBySlot,
  plannedDishes,
  recipesById,
  type PlateBucket,
  type RecipePayload,
} from './plate.js'
import { weekDays, type DayKey, type WeekKey } from './time.js'

export const DIETAS = ['mediterranea', 'ligera', 'proteica', 'adelgazamiento'] as const
export type DietaId = (typeof DIETAS)[number]

export interface Dieta {
  id: DietaId
  label: string
  descripcion: string
  /** Los platos de cada franja, en el orden en que se sirven. */
  platos: Record<MealSlot, Course[]>
  /** La proporción del plato que se busca en comida y cena. */
  objetivo: Record<PlateBucket, number>
  /**
   * Cuántas comidas a la semana de cada proteína: mínimo que se intenta
   * alcanzar y máximo a partir del cual se evita.
   */
  proteinas: Record<ProteinGroup, [min: number, max: number]>
  /** Cuánto se castiga la grasa (aceite, frutos secos, embutido). */
  castigoGrasa: number
  /** Cena sin apenas cereal: ni arroz, ni pasta, ni mucho pan. */
  cenaLigera: boolean
}

const UNICO: Course[] = ['segundo']

export const DIETAS_INFO: Record<DietaId, Dieta> = {
  mediterranea: {
    id: 'mediterranea',
    label: 'Mediterránea',
    descripcion: 'Verdura a diario, legumbre y pescado varias veces, carne con moderación y fruta de postre.',
    platos: { desayuno: UNICO, comida: ['primero', 'segundo', 'postre'], merienda: UNICO, cena: ['segundo', 'postre'] },
    objetivo: { vegetal: 0.5, proteina: 0.25, cereal: 0.25 },
    proteinas: { pescado: [3, 5], legumbre: [3, 4], huevo: [2, 4], carne: [1, 3] },
    castigoGrasa: 1,
    cenaLigera: false,
  },
  ligera: {
    id: 'ligera',
    label: 'Ligera',
    descripcion: 'Cenas suaves de verdura, pescado blanco o huevo, sin arroz ni pasta por la noche, y poca grasa.',
    platos: { desayuno: UNICO, comida: ['primero', 'segundo', 'postre'], merienda: UNICO, cena: ['primero', 'segundo'] },
    objetivo: { vegetal: 0.55, proteina: 0.25, cereal: 0.2 },
    proteinas: { pescado: [3, 6], legumbre: [2, 3], huevo: [2, 4], carne: [0, 2] },
    castigoGrasa: 3,
    cenaLigera: true,
  },
  proteica: {
    id: 'proteica',
    label: 'Proteica',
    descripcion: 'Más proteína en cada comida, con guarnición de verdura y menos cereal.',
    platos: { desayuno: UNICO, comida: ['segundo', 'guarnicion', 'postre'], merienda: UNICO, cena: ['segundo', 'guarnicion'] },
    objetivo: { vegetal: 0.4, proteina: 0.4, cereal: 0.2 },
    proteinas: { pescado: [3, 6], legumbre: [2, 4], huevo: [3, 5], carne: [2, 5] },
    castigoGrasa: 1,
    cenaLigera: false,
  },
  adelgazamiento: {
    id: 'adelgazamiento',
    label: 'Para adelgazar',
    descripcion: 'Mucha verdura, poco cereal y poca grasa, proteína magra y cenas ligeras. De postre, fruta o lácteo.',
    platos: { desayuno: UNICO, comida: ['primero', 'segundo', 'postre'], merienda: UNICO, cena: ['segundo'] },
    objetivo: { vegetal: 0.6, proteina: 0.25, cereal: 0.15 },
    proteinas: { pescado: [3, 6], legumbre: [2, 3], huevo: [2, 4], carne: [0, 2] },
    castigoGrasa: 4,
    cenaLigera: true,
  },
}

/** Un plato que la generación propone poner. */
export interface Propuesta {
  dayKey: DayKey
  slot: MealSlot
  course: Course
  recipeId: string
}

/**
 * De qué va un plato, para no juntar dos iguales en la misma comida: un
 * cocido de primero y una paella de segundo son dos platos únicos.
 */
type Base = 'cereal' | 'legumbre' | 'animal' | 'verdura'

interface Rasgos {
  id: string
  recipe: RecipePayload
  porcion: Record<PlateBucket, number>
  grasa: number
  total: number
  proteinas: Set<ProteinGroup>
  base: Base
  /** Lleva arroz, pasta o pan de sobra para ser plato contundente. */
  conCereal: boolean
  conLegumbre: boolean
}

function rasgosDe(id: string, recipe: RecipePayload): Rasgos {
  const porcion: Record<PlateBucket, number> = { vegetal: 0, proteina: 0, cereal: 0 }
  const proteinas = new Set<ProteinGroup>()
  let grasa = 0
  let total = 0
  let legumbre = 0
  let animal = 0
  for (const ingredient of recipe.ingredients) {
    const peso = gramsOf(ingredient)
    if (!ingredient.group || peso === null) continue
    total += peso
    if (ingredient.group === 'grasa') grasa += peso
    if (ingredient.group === 'legumbre') legumbre += peso
    if (ingredient.group === 'carne' || ingredient.group === 'pescado' || ingredient.group === 'huevo') {
      animal += peso
    }
    if ((PROTEIN_GROUPS as readonly string[]).includes(ingredient.group)) {
      proteinas.add(ingredient.group as ProteinGroup)
    }
    for (const bucket of Object.keys(porcion) as PlateBucket[]) {
      if (GROUPS_BY_BUCKET[bucket].includes(ingredient.group)) porcion[bucket] += peso
    }
  }
  const parte = (gramos: number): number => (total > 0 ? gramos / total : 0)
  const base: Base =
    parte(porcion.cereal) >= 0.35
      ? 'cereal'
      : parte(legumbre) >= 0.25
        ? 'legumbre'
        : parte(animal) >= 0.25
          ? 'animal'
          : 'verdura'
  return {
    id,
    recipe,
    porcion,
    grasa,
    total,
    proteinas,
    base,
    conCereal: parte(porcion.cereal) >= 0.15,
    conLegumbre: parte(legumbre) >= 0.15,
  }
}

/** Sin franjas dichas, una receta vale para comida y cena, no para desayunar. */
function encajaEnFranja(recipe: RecipePayload, slot: MealSlot): boolean {
  if (recipe.slots.length > 0) return recipe.slots.includes(slot)
  return slot === 'comida' || slot === 'cena'
}

/** Sin platos dichos, una receta cuenta como segundo. */
function encajaEnPlato(recipe: RecipePayload, course: Course): boolean {
  if (recipe.courses.length > 0) return recipe.courses.includes(course)
  return course === 'segundo'
}

/** Generador pseudoaleatorio con semilla (mulberry32): mismo número, misma semana. */
function aleatorio(semilla: number): () => number {
  let a = semilla >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const PRINCIPALES: readonly MealSlot[] = ['comida', 'cena']

/**
 * Propone platos para los huecos de una semana.
 *
 * Una franja con algo puesto se considera llena y no se toca. Los días
 * anteriores a `desde` tampoco: planificar el pasado no sirve de nada.
 */
export function generarSemana(input: {
  weekKey: WeekKey
  events: readonly HouseholdEvent[]
  dieta: DietaId
  franjas: readonly MealSlot[]
  desde: DayKey
  semilla: number
}): Propuesta[] {
  const dieta = DIETAS_INFO[input.dieta]
  const azar = aleatorio(input.semilla)
  const plan = planBySlot(input.events)
  const recetas = [...recipesById(input.events)].map(([id, recipe]) => rasgosDe(id, recipe))
  const porId = new Map(recetas.map((r) => [r.id, r]))

  const vecesProteina = Object.fromEntries(PROTEIN_GROUPS.map((g) => [g, 0])) as Record<ProteinGroup, number>
  const usos = new Map<string, number>()
  const anotar = (id: string): void => {
    usos.set(id, (usos.get(id) ?? 0) + 1)
    for (const group of porId.get(id)?.proteinas ?? []) vecesProteina[group] += 1
  }

  // Lo que ya hay puesto esa semana cuenta para la variedad.
  const dias = weekDays(input.weekKey)
  for (const dayKey of dias) {
    for (const slot of MEAL_SLOTS) {
      for (const dish of plannedDishes(plan, dayKey, slot)) if (dish.recipeId) anotar(dish.recipeId)
    }
  }

  const propuestas: Propuesta[] = []

  for (const dayKey of dias) {
    if (dayKey < input.desde) continue
    const delDia = new Set<string>()
    const plato: Record<PlateBucket, number> = { vegetal: 0, proteina: 0, cereal: 0 }
    const sumar = (r: Rasgos): void => {
      for (const b of Object.keys(plato) as PlateBucket[]) plato[b] += r.porcion[b]
    }
    for (const slot of PRINCIPALES) {
      for (const dish of plannedDishes(plan, dayKey, slot)) {
        const r = dish.recipeId ? porId.get(dish.recipeId) : undefined
        if (r) {
          sumar(r)
          delDia.add(r.id)
        }
      }
    }

    for (const slot of MEAL_SLOTS) {
      if (!input.franjas.includes(slot)) continue
      if (plannedDishes(plan, dayKey, slot).length > 0) continue

      const enLaComida: Rasgos[] = []
      for (const course of dieta.platos[slot]) {
        const candidatas = recetas.filter(
          (r) => encajaEnFranja(r.recipe, slot) && encajaEnPlato(r.recipe, course) && !delDia.has(r.id),
        )
        let mejor: Rasgos | null = null
        let mejorPuntos = -Infinity
        for (const r of candidatas) {
          const puntos = puntuar(r, { dieta, slot, course, plato, vecesProteina, usos, enLaComida }) + azar() * 1.5
          if (puntos > mejorPuntos) {
            mejor = r
            mejorPuntos = puntos
          }
        }
        if (!mejor || mejorPuntos === -Infinity) continue

        propuestas.push({ dayKey, slot, course, recipeId: mejor.id })
        anotar(mejor.id)
        delDia.add(mejor.id)
        if (course !== 'postre') enLaComida.push(mejor)
        if (PRINCIPALES.includes(slot)) sumar(mejor)
      }
    }
  }

  return propuestas
}

function puntuar(
  r: Rasgos,
  ctx: {
    dieta: Dieta
    slot: MealSlot
    course: Course
    plato: Record<PlateBucket, number>
    vecesProteina: Record<ProteinGroup, number>
    usos: Map<string, number>
    enLaComida: Rasgos[]
  },
): number {
  const { dieta, slot, course, plato, vecesProteina, usos, enLaComida } = ctx
  let puntos = 0

  // Variedad: repetir plato en la semana sale caro.
  puntos -= (usos.get(r.id) ?? 0) * 8

  // Proteína de la semana: empujar hacia el mínimo, frenar pasado el máximo.
  for (const group of r.proteinas) {
    const [min, max] = dieta.proteinas[group]
    const veces = vecesProteina[group]
    if (max === 0) return -Infinity
    if (veces >= max) puntos -= 6
    else if (veces < min) puntos += 3
  }

  const total = r.total || 1
  puntos -= dieta.castigoGrasa * (r.grasa / total) * 10

  if (PRINCIPALES.includes(slot) && course !== 'postre') {
    // Cómo queda el plato del día si se añade esta receta.
    const despues = {
      vegetal: plato.vegetal + r.porcion.vegetal,
      proteina: plato.proteina + r.porcion.proteina,
      cereal: plato.cereal + r.porcion.cereal,
    }
    const suma = despues.vegetal + despues.proteina + despues.cereal
    if (suma > 0) {
      let desvio = 0
      for (const b of Object.keys(despues) as PlateBucket[]) desvio += Math.abs(despues[b] / suma - dieta.objetivo[b])
      puntos -= desvio * 6
    }

    if (dieta.cenaLigera && slot === 'cena' && r.porcion.cereal / total > 0.2) puntos -= 5
    if (dieta.objetivo.cereal <= 0.15 && r.conCereal) puntos -= r.base === 'cereal' ? 7 : 3

    // Que los platos de una misma comida se complementen: no dos de arroz o
    // pasta, no dos de legumbre, y el segundo con la proteína.
    for (const otro of enLaComida) {
      if (otro.base === r.base) puntos -= r.base === 'verdura' ? 2 : 8
      if (otro.conCereal && r.conCereal) puntos -= 6
      if (otro.conLegumbre && r.conLegumbre) puntos -= 8
      for (const group of r.proteinas) if (group !== 'huevo' && otro.proteinas.has(group)) puntos -= 4
    }
    if (enLaComida.length > 0 && course === 'segundo' && (r.base === 'cereal' || r.base === 'verdura')) {
      puntos -= 3
    }
    if (course === 'primero' && r.base === 'animal') puntos -= 4
    if (dieta.objetivo.proteina >= 0.35 && course === 'segundo') puntos += (r.porcion.proteina / total) * 6
  }

  return puntos
}

/** Cuántos huecos rellenaría, para decirlo antes de generar. */
export function huecosLibres(input: {
  weekKey: WeekKey
  events: readonly HouseholdEvent[]
  franjas: readonly MealSlot[]
  desde: DayKey
}): number {
  const plan = planBySlot(input.events)
  let libres = 0
  for (const dayKey of weekDays(input.weekKey)) {
    if (dayKey < input.desde) continue
    for (const slot of input.franjas) if (plannedDishes(plan, dayKey, slot).length === 0) libres += 1
  }
  return libres
}
